import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Grid, FormLabel, Skeleton, Stack, Switch, FormGroup, FormControl, FormControlLabel } from '@mui/material';
import FilledChart from '../Charts/FilledChart';
import BarChart from '../Charts/BarChart';
import Api from '../../network/Api';
import RadarChart from '../Charts/RadarChart';
import HeatmapChart from '../Charts/HeatmapChart';
import ImpactChart from '../Charts/ImpactChart';
import GaugeChart from '../Charts/GaugeChart';
import StatusIndicator from './StatusIndicator';
import { Form } from 'react-router-dom';

const Placeholder = () => {
  return <Stack spacing={1}>
    <Skeleton height={30} />
    <Skeleton variant="rectangular" height={100} />
  </Stack>
}

const Dashboard = () => {

  const [values, setValues] = useState();
  const [severitiesField, setSeveritiesField] = useState<"perceptions"|"count">("perceptions");

  useEffect(() => {
    Api.get(`/dashboard`).then(setValues);
  }, []);

  const changeRateLabel = (changeRate: number) => {
    let percent = changeRate * 100;
    return(changeRate != null && <FormLabel>
      <span className={percent >= 0 ? 'text-success' : 'text-error'} style={{fontFamily: 'var(--header-font)'}}>
        {percent >= 0 ? '+' : '-'}{Math.abs(percent).toFixed(1)}% this month
      </span></FormLabel>);
  }

  return <Box sx={{overflowY: 'auto', flexGrow: 1, width: "100%", py: 5}}>
    <Container id="dashboard" maxWidth="xl">
      <Grid container spacing={3}>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Lessons learned</h3>
            { values ? <>
              <h2 className='text-light'>{typeof(values.lessons?.total) !== "undefined" ? values.lessons.total : '-'} <FormLabel><span className="text-secondary">total</span></FormLabel></h2>
              { changeRateLabel(values.lessons?.change_rate) }

              <div className='area-chart'>{<FilledChart series={values.lessons?.series ?? []} />}</div>
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Solutions</h3>
            { values ? <>
              <h2 className='text-light'>{typeof(values.solutions?.approved) !== "undefined" ? values.solutions.approved : '-'} <FormLabel><span className="text-secondary">delivered</span></FormLabel></h2>
              <div className='area-chart'>{<BarChart series={values.solutions?.series ?? []} />}</div>
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h3>Perception index</h3>
              { values && <FormLabel><span className={`text-${values.perception_index.valid === null ? "secondary" : values.perception_index.value ? "success" : "error"}`} style={{fontFamily: 'var(--header-font)'}}>{values.perception_index.valid == null ? "Not enough data" : `${values.perception_index.valid ? "high" : "low"} validity`}</span></FormLabel> }
            </Stack>
            { values ? <>
              <div className='area-chart'><GaugeChart value={values.perception_index.score} /></div>
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h3>Severities</h3>
              <FormGroup row sx={{pb:  "0 !important", alignItems: "center", gap: "0 !important"}}>
                <FormControlLabel
                  control={<Switch checked={severitiesField === 'perceptions'} color="secondary" onChange={(e, on) => setSeveritiesField(on ? "perceptions" : "count")} />}
                  label={<FormLabel>{severitiesField === 'perceptions' ? "Subjective" : "Objective"} score</FormLabel>}
                  labelPlacement="start" />
              </FormGroup>
            </Stack>
            { values ? <>
              <HeatmapChart categories={values.categories ?? []} field={severitiesField} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Distributed risk score</h3>
            { values ? <>
             <ImpactChart set={values.lessons?.scores ?? []} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 4}}>
          <Card>
            <h3>Cause map</h3>
            { values ? <>
              <RadarChart categories={values.categories ?? []} />
            </> : <Placeholder /> }
          </Card>
        </Grid>

      </Grid>

    </Container>
  </Box>;
};

export default Dashboard;